/**
 * Gravure personnalisée — Bijoux Deby
 *
 * Gère le module de gravure d'une fiche produit :
 *  - aperçu SVG en direct du texte sur la plaque (masqué à la zone gravable)
 *  - ajustement automatique de la taille du texte
 *  - choix de la typographie
 *  - validation avant l'ajout au panier
 *
 * Les valeurs saisies sont envoyées comme propriétés de ligne de commande
 * (properties[...]) et apparaissent donc dans le panier et la commande.
 */

if (!customElements.get('gravure-personnalisation')) {
  customElements.define(
    'gravure-personnalisation',
    class GravurePersonnalisation extends HTMLElement {
      // Caractères réalisables par la graveuse (lettres accentuées, chiffres, ponctuation simple).
      static CARACTERES_INTERDITS = /[^A-Za-zÀ-ÖØ-öø-ÿŒœ0-9 &'’\-.,!?°]/g;

      // Taille de référence utilisée pour mesurer le texte avant mise à l'échelle.
      static TAILLE_MESURE = 100;

      connectedCallback() {
        this.input = this.querySelector('[data-gravure-texte]');
        this.compteur = this.querySelector('[data-gravure-compteur]');
        this.policeInput = this.querySelector('[data-gravure-police]');
        this.boutonsPolice = Array.from(this.querySelectorAll('[data-gravure-font]'));

        // L'aperçu SVG vit maintenant dans la galerie média (product__media-wrapper),
        // donc en dehors de cet élément : on le retrouve par id plutôt que par
        // querySelector interne. uid partagé via data-uid entre les deux rendus Liquid.
        this.uid = this.dataset.uid;
        this.texteSvg = document.getElementById('gravure-svg-texte-' + this.uid);
        this.cheminSvg = document.getElementById('gravure-svg-chemin-' + this.uid);

        this.form = this.closest('form');
        this.productForm = this.closest('product-form');

        this.largeurMax = parseFloat(this.dataset.largeurMax) || 350;
        this.tailleMax = parseFloat(this.dataset.tailleMax) || 88;
        this.tailleMin = parseFloat(this.dataset.tailleMin) || 30;

        if (this.input) {
          this.input.addEventListener('input', this.onSaisie.bind(this));
          this.input.addEventListener('blur', () => this.valider());
        }

        this.boutonsPolice.forEach((bouton) => {
          bouton.addEventListener('click', () => this.choisirPolice(bouton));
        });

        // La capture sur un ancêtre garantit de passer avant le gestionnaire de Dawn.
        if (this.form) {
          (this.productForm || document).addEventListener('submit', this.onSubmit.bind(this), true);
          this.form.addEventListener('click', this.onClickCapture.bind(this), true);
        }

        this.rendre();
        this.majEtatAchatExpress();

        // Les polices Google arrivent après le premier rendu : on réajuste.
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.rendre());
        }
        window.addEventListener('load', () => this.rendre());
      }

      onSaisie() {
        const propre = this.input.value.replace(GravurePersonnalisation.CARACTERES_INTERDITS, '');
        if (propre !== this.input.value) {
          const position = this.input.selectionStart - 1;
          this.input.value = propre;
          this.input.setSelectionRange(position, position);
        }

        if (this.compteur) this.compteur.textContent = this.input.value.length;
        if (this.input.value.trim()) this.masquerErreur();
        this.rendre();
        this.majEtatAchatExpress();
      }

      choisirPolice(bouton) {
        this.boutonsPolice.forEach((autre) => autre.setAttribute('aria-pressed', String(autre === bouton)));
        if (this.policeInput) this.policeInput.value = bouton.dataset.gravureFont;
        this.rendre();
      }

      get policeActive() {
        return this.boutonsPolice.find((b) => b.getAttribute('aria-pressed') === 'true') || this.boutonsPolice[0];
      }

      /** Redessine le texte gravé et ajuste sa taille pour tenir sur la plaque. */
      rendre() {
        if (!this.texteSvg || !this.cheminSvg) return;

        const bouton = this.policeActive;
        if (bouton) {
          this.texteSvg.setAttribute('font-family', bouton.dataset.gravureFamille);
          this.texteSvg.setAttribute('font-style', bouton.dataset.gravureStyle || 'normal');
          this.texteSvg.setAttribute('font-weight', bouton.dataset.gravureGraisse || '400');
        }

        // Sans saisie, on affiche un exemple estompé plutôt qu'une plaque vide.
        const saisie = this.input ? this.input.value : '';
        this.cheminSvg.textContent = saisie || this.dataset.exemple || '';
        this.texteSvg.setAttribute('fill-opacity', saisie ? '0.92' : '0.3');

        this.texteSvg.setAttribute('font-size', GravurePersonnalisation.TAILLE_MESURE);
        let largeur = 0;
        try {
          largeur = this.texteSvg.getComputedTextLength();
        } catch (e) {
          largeur = 0;
        }

        let taille = this.tailleMax;
        if (largeur > 0) {
          const ideale = (GravurePersonnalisation.TAILLE_MESURE * this.largeurMax) / largeur;
          taille = Math.max(this.tailleMin, Math.min(this.tailleMax, ideale));
        }
        this.texteSvg.setAttribute('font-size', Math.round(taille));
      }

      estValide() {
        return !this.input || this.input.value.trim().length > 0;
      }

      valider() {
        if (this.estValide()) {
          this.masquerErreur();
          return true;
        }
        this.classList.add('gravure--invalid');
        return false;
      }

      masquerErreur() {
        this.classList.remove('gravure--invalid');
      }

      /** Neutralise « Acheter maintenant » tant que la gravure n'est pas renseignée. */
      majEtatAchatExpress() {
        if (!this.productForm) return;
        this.productForm.classList.toggle('product-form--gravure-invalid', !this.estValide());
      }

      onSubmit(event) {
        if (event.target !== this.form) return;
        if (this.valider()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.majEtatAchatExpress();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.input.focus();
      }

      onClickCapture(event) {
        const bouton = event.target.closest('.shopify-payment-button__button');
        if (!bouton || this.estValide()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.valider();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.input.focus();
      }
    }
  );
}
